import { toast as baseToast, cssTransition } from 'react-toastify';
import type { Id, ToastContent, ToastOptions, TypeOptions } from 'react-toastify';
import './toast.css';

type ToastFn<TData> = (content: ToastContent<TData>, options?: ToastOptions) => Id;

// Same enter as the default Bounce; exit uses toast.css's 300ms variant instead of the vendor's 700ms one.
const QuickExitBounce = cssTransition({
    enter: 'Toastify--animate Toastify__bounce-enter',
    exit: 'Toastify--animate Toastify__quick-exit',
    appendPosition: true
});

const THROTTLE_MS = 3000;
let lastShownAt = 0;

function isThrottled(): boolean {
    const now = Date.now();
    if (now - lastShownAt < THROTTLE_MS) return true;
    lastShownAt = now;
    return false;
}

// Ignore calls that happen within THROTTLE_MS of the previous one.
function throttle<TData = unknown>(fn: ToastFn<TData>, _type?: TypeOptions): ToastFn<TData> {
    return (content, options) => {
        if (isThrottled()) return undefined as unknown as Id;
        return fn(content, options);
    };
}

// Track the toast currently on screen so a repeat call can dismiss it (playing its exit animation) before showing the next one.
let activeId: Id | null = null;

function dismissAndShow<TData = unknown>(fn: ToastFn<TData>, _type?: TypeOptions): ToastFn<TData> {
    return (content, options) => {
        let previousId = activeId;
        activeId = fn(content, { transition: QuickExitBounce, ...options });
        setTimeout(() => {
            if (previousId !== null && baseToast.isActive(previousId)) {
                baseToast.dismiss(previousId);
            }
        }, 300); // ensure exit animation plays before dismissing
        return activeId;
    };
}

// Dismiss the previous toast (with its exit animation) before showing the next, and still ignore calls within THROTTLE_MS of the last shown one.
function combine<TData = unknown>(fn: ToastFn<TData>, type?: TypeOptions): ToastFn<TData> {
    return dismissAndShow(throttle(fn), type);
}

const strategies = { throttle, dismiss: dismissAndShow, combine } as const;
export type ToastDedupeStrategy = keyof typeof strategies;

function createToast(strategy: ToastDedupeStrategy) {
    const wrap = strategies[strategy];
    return Object.assign(wrap(baseToast), baseToast, {
        success: wrap(baseToast.success, 'success'),
        error: wrap(baseToast.error, 'error'),
        info: wrap(baseToast.info, 'info'),
        warning: wrap(baseToast.warning, 'warning'),
        warn: wrap(baseToast.warn, 'warning')
    });
}

// Pick whichever dedupe strategy fits the call site: throttleToast, dismissToast or combinedToast.
export const throttleToast = createToast('throttle');
export const dismissToast = createToast('dismiss');
export const combinedToast = createToast('combine');

export const toast = dismissToast;
